import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { flatMap, map, switchMap } from 'rxjs/operators';

import {
  YesNoCancelDialogData,
  YesNoCancelModalResult
} from '../../notify/components/yes-no-cancel-modal/yes-no-cancel-modal.component';
import { NotifyService } from '../../notify/services/notify.service';

export interface HasUnsavedData {
  hasUnsavedData(): boolean;
  saveData(): Observable<any>;
}

@Injectable()
export class UnsavedDataGuard implements CanDeactivate<HasUnsavedData> {

  modalData: YesNoCancelDialogData = {
    contentKey: 'APP.GENERAL.UNSAVED_CHANGES',
    noKey: 'APP.GENERAL.BACK_WITHOUT_SAVE',
    yesKey: 'APP.GENERAL.SAVE',
  };

  constructor(private notifyService: NotifyService) { }

  canDeactivate(component: HasUnsavedData,
                currentRoute: ActivatedRouteSnapshot,
                currentState: RouterStateSnapshot,
                nextState?: RouterStateSnapshot): Observable<boolean> {
    return this.redirectWithPrompt(component);
  }

  private redirectSilently(component: HasUnsavedData): Observable<boolean> {
    return component.saveData()
      .pipe(
        map(() => true),
      );
  }

  private redirectWithPrompt(component: HasUnsavedData): Observable<boolean> {
    return of(component.hasUnsavedData())
      .pipe(
        switchMap(hasUnsaved => {
          if (hasUnsaved) {
            return this.prompt(component);
          } else {
            return of(true);
          }
        })
      );
  }

  private prompt(component: HasUnsavedData): Observable<boolean> {
    return this.notifyService.yesNoCancelModal(this.modalData)
      .pipe(
        map(val => val != null ? val : YesNoCancelModalResult.CANCEL),
        flatMap(result => this.redirectByResult(result, component))
      );
  }

  private redirectByResult(result: YesNoCancelModalResult, component: HasUnsavedData): Observable<boolean> {
    switch (result) {
      case YesNoCancelModalResult.CANCEL: {
        return of(false);
      }
      case YesNoCancelModalResult.NO: {
        return of(true);
      }
      case YesNoCancelModalResult.YES: {
        return component.saveData()
          .pipe(
            map(() => true),
          );
      }
    }
  }
}
